import { Divider } from '@/components/ui/divider'
import { HStack } from '@/components/ui/hstack'
import { Image } from '@/components/ui/image'
import { Pressable } from '@/components/ui/pressable'
import { Text } from '@/components/ui/text'
import { VStack } from '@/components/ui/vstack'
import { FormBillingAccount } from '@/redux/slices/formBillingAccountSlice'
import { ShallowFormCooperator } from '@/redux/slices/formCooperatorSlice'
import { ShallowFormRole } from '@/redux/slices/formRoleSlice'
import { ShallowForm } from '@/redux/slices/formSlice'
import { UserStatus } from '@/typings/domain'
import { useHeaderHeight } from '@react-navigation/elements'
import { Theme, useTheme } from '@react-navigation/native'
import { FlashList } from '@shopify/flash-list'
import { useRouter } from 'expo-router'
import { keyBy } from 'lodash-es'
import { ChevronRightIcon, CircleUserIcon } from 'lucide-react-native'
import { useCallback, useMemo } from 'react'
import { Platform } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

type Props = {
  form: ShallowForm
  formBillingAccount: FormBillingAccount
  roles: ShallowFormRole[]
  cooperators: ShallowFormCooperator[]
  refreshing: boolean
  onRefresh: () => void
  onLoadMore: () => void
}

export default function FormCooperators({
  form,
  formBillingAccount,
  roles,
  cooperators,
  refreshing,
  onRefresh,
  onLoadMore,
}: Props) {
  const insets = useSafeAreaInsets()
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const headerHeight = Platform.OS === 'ios' ? useHeaderHeight() : 0
  const theme = useTheme()
  const router = useRouter()

  const normalizedData = useMemo<NormalizedFormCooperator[]>(() => {
    if (!form.owner) return []
    const formRolesMap = keyBy(roles, 'id')
    return [
      {
        id: form.owner?.id,
        displayName: form.owner?.displayName,
        avatarUrl: form.owner?.avatarUrl,
        email: form.owner?.email,
        formRole: null,
        isFormOwner: true,
      },
      ...cooperators.map(cooperator => ({
        id: cooperator.id,
        displayName: cooperator.displayName,
        avatarUrl: cooperator.avatarUrl,
        email: cooperator.email,
        formRole: formRolesMap[cooperator.formRole.id],
        isExternal: formBillingAccount?.isOrgPlanOrTrial && cooperator.billingAccount?.id !== formBillingAccount.id,
        isDisabled: cooperator.status === UserStatus.Disabled,
      })),
    ]
  }, [cooperators, form.owner, formBillingAccount.id, formBillingAccount?.isOrgPlanOrTrial, roles])

  const handleCooperatorPress = useCallback(
    (cooperator: NormalizedFormCooperator) => {
      router.push(`/forms/${form.token}/setting/cooperators/${cooperator.id}`)
    },
    [router, form.token]
  )

  const ListHeader = useCallback(
    () => <Text className="text-typography-500 px-6 pb-4">添加成员作为管理员或维护员，协同管理表单和数据。</Text>,
    []
  )

  if (!form.owner) return null

  return (
    <FlashList
      contentInsetAdjustmentBehavior="automatic"
      contentContainerStyle={{ paddingTop: 16, paddingBottom: insets.bottom + 16 }}
      data={normalizedData}
      keyExtractor={item => item.id}
      ListHeaderComponent={ListHeader}
      renderItem={({ item, index }) => (
        <CooperatorItem
          item={item}
          isLast={index === normalizedData.length - 1}
          theme={theme}
          onPress={handleCooperatorPress}
        />
      )}
      refreshing={refreshing}
      onRefresh={onRefresh}
      progressViewOffset={headerHeight}
      onEndReached={onLoadMore}
      onEndReachedThreshold={0.1}
    />
  )
}

type NormalizedFormCooperator = {
  id: string
  displayName: string
  avatarUrl?: Maybe<string>
  email?: Maybe<string>
  formRole: Maybe<ShallowFormRole> | undefined
  isFormOwner?: boolean
  isExternal?: boolean
  isDisabled?: boolean
}

type CooperatorItemProps = {
  item: NormalizedFormCooperator
  isLast: boolean
  theme: Theme
  onPress?: (item: NormalizedFormCooperator) => void
}

function CooperatorItem({ item, isLast, theme, onPress }: CooperatorItemProps) {
  return (
    <Pressable className="data-[active=true]:bg-background-100" onPress={() => onPress?.(item)}>
      <HStack className="px-6 py-4 items-center" space="md">
        {item.avatarUrl ? (
          <Image source={{ uri: item.avatarUrl }} className="w-8 h-8 rounded-full" alt={item.displayName} />
        ) : (
          <CircleUserIcon size={28} color={theme.colors.primary} />
        )}
        <VStack space="sm" className="flex-1">
          <Text size="body" className={item.isDisabled ? 'text-secondary-label' : 'text-label'} isTruncated>
            {item.displayName}{' '}
            {item.isExternal && (
              <Text size="subhead" className="text-secondary-label">
                （外部联系人）
              </Text>
            )}
          </Text>
          <Text size="subhead" className="text-secondary-label" isTruncated>
            {item.isFormOwner ? '表单创建者' : item.formRole?.name}
            {item.email ? ` | ${item.email}` : ''}
          </Text>
        </VStack>
        {onPress && <ChevronRightIcon size={20} color={theme.colors.border} />}
      </HStack>
      {!isLast && <Divider />}
    </Pressable>
  )
}
